import { useCallback, useState } from 'react';

import useFetch from 'hooks/useFetch';
import useRetry from 'hooks/useRetry';

import useTranslation from './hooks/useTranslation';

import { CHEMICAL_DB_API_HOST, JSON_HEADERS, MIXES_API } from './constants';
import type { ArchiveMix, CreateMix, DuplicateMix, FetchMix, Mix, UpdateMix } from './types';

const useMix = () => {
    const [isLoading, setIsLoading] = useState(false);
    const { authenticatedDelete, authenticatedGet, authenticatedPost, authenticatedPut } = useFetch(
        { apiHost: CHEMICAL_DB_API_HOST, headers: JSON_HEADERS }
    );
    const { withRetry } = useRetry();
    const { ARCHIVE_FAILED, CONNECTION_ERROR, SAVE_FAILED } = useTranslation();

    const fetchMix: FetchMix = useCallback(
        async ({ id }) => {
            try {
                setIsLoading(true);

                return await withRetry(
                    () => authenticatedGet<Mix>(`${MIXES_API}/${id}`),
                    CONNECTION_ERROR,
                    () => fetchMix({ id })
                );
            } finally {
                setIsLoading(false);
            }
        },

        [authenticatedGet, withRetry, CONNECTION_ERROR]
    );

    const createMix: CreateMix = useCallback(
        async ({ mix }) => {
            try {
                setIsLoading(true);

                return await withRetry(
                    () => authenticatedPost<Mix>(MIXES_API, JSON.stringify(mix)),
                    SAVE_FAILED,
                    () => createMix({ mix })
                );
            } finally {
                setIsLoading(false);
            }
        },

        [authenticatedPost, withRetry, SAVE_FAILED]
    );

    const updateMix: UpdateMix = useCallback(
        async ({ id, mix }) => {
            try {
                setIsLoading(true);

                return await withRetry(
                    () => authenticatedPut<Mix>(`${MIXES_API}/${id}`, JSON.stringify(mix)),
                    SAVE_FAILED,
                    () => updateMix({ id, mix })
                );
            } finally {
                setIsLoading(false);
            }
        },

        [authenticatedPut, withRetry, SAVE_FAILED]
    );

    const duplicateMix: DuplicateMix = useCallback(
        async ({ id }) => {
            try {
                setIsLoading(true);

                return await withRetry(
                    () => authenticatedPost<Mix>(`${MIXES_API}/${id}/duplicate`, '{}'),
                    SAVE_FAILED,
                    () => duplicateMix({ id })
                );
            } finally {
                setIsLoading(false);
            }
        },

        [authenticatedPost, withRetry, SAVE_FAILED]
    );

    const archiveMix: ArchiveMix = useCallback(
        async ({ id }) => {
            try {
                setIsLoading(true);

                const result = await withRetry(
                    () => authenticatedDelete(`${MIXES_API}/${id}`),
                    ARCHIVE_FAILED,
                    () => archiveMix({ id })
                );

                return result !== null;
            } finally {
                setIsLoading(false);
            }
        },

        [authenticatedDelete, withRetry, ARCHIVE_FAILED]
    );

    return {
        archiveMix,
        createMix,
        duplicateMix,
        fetchMix,
        isLoading,
        updateMix
    };
};

export default useMix;
